import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Image as ImageIcon, Palette, Ruler, Sun, ExternalLink, AlertTriangle, CheckCircle2, Pin } from 'lucide-react';
import { API, STAGES, fullImg } from './constants';

/** V15.25 — Surcharge d'un rôle pour CE lot uniquement (le projet reste la source de vérité). */
const LotOverrideSelect = ({ projectId, lotId, role, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const save = async (materialId) => {
    setBusy(true);
    try {
      await axios.put(`${API}/projects/${projectId}/lots/${lotId}/material-overrides`,
        { role_key: role.role_key, material_id: materialId || null }, { withCredentials: true });
      toast.success(materialId ? `Surcharge appliquée à ce lot : ${role.label}` : `Surcharge retirée : ${role.label} suit le projet`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Surcharge impossible');
    } finally { setBusy(false); }
  };
  return (
    <select value={role.override_material_id || ''} onChange={(e) => save(e.target.value)} disabled={busy}
            className="ml-auto max-w-[220px] text-[10.5px] rounded px-1.5 py-0.5 bg-black/30 outline-none"
            style={{ border: `1px solid ${role.override_material_id ? '#D9A05B' : 'var(--c-border)'}`, color: role.override_material_id ? 'var(--c-gold)' : 'var(--c-text-2)' }}
            title="Surcharger ce rôle pour ce lot uniquement" data-testid={`lot-override-select-${role.role_key}`}>
      <option value="">— Suit le projet —</option>
      {role.candidates.map((c) => <option key={c.material_id} value={c.material_id}>{c.is_retained ? '★ ' : ''}{c.name}</option>)}
    </select>
  );
};

const Section = ({ icon: Icon, title, action, children, testid }) => (
  <div className="rounded-lg p-3" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid var(--c-border)' }} data-testid={testid}>
    <div className="flex items-center justify-between gap-2 mb-2">
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--c-text-2)' }}><Icon className="w-3.5 h-3.5" style={{ color: 'var(--c-gold)' }} />{title}</span>
      {action}
    </div>
    {children}
  </div>
);

const EditLink = ({ onClick, label, testid }) => (
  <button type="button" onClick={onClick} className="inline-flex items-center gap-1 text-[10.5px] underline-offset-2 hover:underline" style={{ color: 'var(--c-gold)' }} data-testid={testid}>
    {label} <ExternalLink className="w-3 h-3" />
  </button>
);

const Row = ({ k, v }) => (
  <div className="flex items-start justify-between gap-3 text-[11.5px] py-0.5">
    <span style={{ color: 'var(--c-text-muted)' }}>{k}</span>
    <span className="text-right font-medium" style={{ color: 'var(--c-text)' }}>{v}</span>
  </div>
);

const CUT = { 1: 'Pleine hauteur', 0.5: 'Mi-hauteur', 0.3: 'Basse' };
const DOORS = { closed: 'fermées', open45: 'ouvertes 45°', open90: 'ouvertes 90°' };

/** V15.21 — Zone 2b : « Ce que l'IA recevra » pour l'étape active, avec liens d'édition vers la source. */
export const ActiveInputsPanel = ({ projectId, lotId, stage, data, onChangeReference, onRefresh }) => {
  const navigate = useNavigate();
  const e = data?.stages?.[stage];
  if (!e) return null;
  const meta = STAGES.find((s) => s.id === stage);
  const goPlan = () => navigate(`/projects/${projectId}?section=maquette&lot_id=${lotId}`);
  const goMat = () => navigate(`/materialtheque?project=${projectId}`);
  const goCfg = () => navigate(`/projects/${projectId}/prompt-config`);
  const geo = data.stages.whitebox?.geometry || {};
  const outdoor = geo.outdoor_rooms || [];

  return (
    <div className="space-y-2" data-testid="active-inputs-panel">
      <div className="flex items-center justify-between">
        <span className="hud-eyebrow hud-eyebrow-gold">Ce que {stage === 'whitebox' ? 'le moteur' : "l'IA"} recevra — étape {meta.n} · {meta.label}</span>
        <span className="text-[10.5px]" style={{ color: 'var(--c-text-faint)' }}>{meta.hint}</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {e.base && (
          <Section icon={ImageIcon} title="Image de base (référence étape précédente)" testid="inputs-base-image"
                   action={<EditLink onClick={() => onChangeReference?.(e.base.stage)} label="Changer d'ancrage" testid="inputs-change-anchor" />}>
            {e.base.ready ? (
              <div className="flex gap-2">
                <img src={fullImg(e.base.image_url)} alt="" className="w-28 h-20 object-contain rounded" style={{ background: '#fff' }} />
                <div className="flex-1 text-[11px]">
                  <Row k="Rendu" v={<span className="font-mono">{e.base.job_id}</span>} />
                  {e.base.vue_angle && <Row k="Caméra" v={e.base.vue_angle} />}
                  {typeof e.base.fidelity_score === 'number' && <Row k="Conformité plan" v={`${e.base.fidelity_score} %`} />}
                  <p className="mt-1" style={{ color: 'var(--c-text-faint)' }}>Géométrie, cadrage et caméra figés : seul le contenu de l'étape change.</p>
                </div>
              </div>
            ) : (
              <p className="inline-flex items-center gap-1.5 text-[11.5px]" style={{ color: '#FDE68A' }} data-testid="inputs-base-missing"><AlertTriangle className="w-3.5 h-3.5" /> Aucune référence ancrée à l'étape {meta.n - 1} : ancrez d'abord un rendu {STAGES[meta.n - 2].short.toLowerCase()}.</p>
            )}
          </Section>
        )}
        {stage === 'whitebox' && (
          <Section icon={Ruler} title="Géométrie du tracé" testid="inputs-geometry" action={<EditLink onClick={goPlan} label="Éditer le plan" testid="edit-plan-link" />}>
            <Row k="Murs / ouvertures" v={`${geo.walls || 0} / ${geo.openings || 0}`} />
            <Row k="Pièces" v={`${geo.rooms || 0}${geo.named_rooms?.length ? ` · ${geo.named_rooms.slice(0, 6).join(', ')}${geo.named_rooms.length > 6 ? '…' : ''}` : ''}`} />
            <Row k="Escaliers / éléments fixes" v={`${geo.stairs || 0} / ${geo.fixtures || 0}`} />
            <Row k="Façades pleine hauteur forcées" v={geo.full_height_walls || 0} />
            <Row k="Échelle calibrée" v={geo.calibrated ? 'oui' : 'non'} />
          </Section>
        )}
        {(stage === 'whitebox' || stage === 'textured') && (
          <Section icon={Ruler} title="Coupe, format & extérieurs" testid="inputs-geometry-settings" action={<EditLink onClick={goPlan} label="Éditer le plan" testid={stage === 'textured' ? 'edit-plan-link' : 'edit-plan-link-2'} />}>
            {data.stages.whitebox?.settings ? (
              <>
                <Row k="Format" v={data.stages.whitebox.settings.aspect === '16:9' ? '16:9 · 1920×1080' : '1:1 · 1024²'} />
                <Row k="Coupe des façades" v={CUT[data.stages.whitebox.settings.peripheral_cut] || `${Math.round((data.stages.whitebox.settings.peripheral_cut || 0.5) * 100)} %`} />
                <Row k="Façades du fond" v={data.stages.whitebox.settings.back_walls_full ? 'pleine hauteur' : 'coupées'} />
                <Row k="Portes" v={DOORS[data.stages.whitebox.settings.door_style] || 'fermées'} />
              </>
            ) : <p className="text-[11px]" style={{ color: 'var(--c-text-faint)' }}>Réglages fixés à la génération de la maquette (coupe, format 1:1 / 16:9, portes).</p>}
            <Row k="Espaces extérieurs" v={outdoor.length ? outdoor.map((o) => `${o.label} (${o.type})`).join(', ') : 'aucun — rien ne sera inventé'} />
          </Section>
        )}
        {(stage === 'textured' || stage === 'furnished') && (
          <Section icon={Palette} title={stage === 'textured' ? 'Matériaux retenus (sols, murs, menuiseries)' : 'Mobilier retenu (Matériauthèque)'} testid="inputs-materials"
                   action={<span className="inline-flex items-center gap-3"><EditLink onClick={goMat} label="Éditer dans la Matériauthèque" testid="edit-materiautheque-link" />{stage === 'furnished' && <EditLink onClick={goCfg} label="Consignes libres par pièce" testid="edit-room-instructions-link" />}</span>}>
            {e.materials?.length ? (
              <ul className="space-y-1 max-h-44 overflow-auto pr-1">
                {e.materials.map((m, i) => (
                  <li key={`${m.label}-${i}`} className="text-[11px] flex gap-1.5 items-start" data-testid={`inputs-material-${m.role_key || i}`}>
                    {m.status === 'override' ? <Pin className="w-3 h-3 shrink-0 mt-0.5" style={{ color: 'var(--c-gold)' }} />
                      : m.status === 'empty' || m.status === 'ambiguous' ? <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" style={{ color: '#FDE68A' }} /> : <CheckCircle2 className="w-3 h-3 shrink-0 mt-0.5" style={{ color: '#86EFAC' }} />}
                    <span className="min-w-0"><span style={{ color: 'var(--c-text-muted)' }}>{m.label}{m.status === 'override' ? ' (surcharge lot)' : ''} :</span> <span style={{ color: 'var(--c-text)' }}>{m.sent_text || (m.status === 'empty' ? 'emplacement vide (défaut IA)' : m.status === 'ambiguous' ? 'plusieurs choix non arbitrés' : '—')}</span></span>
                    {m.candidates?.length > 0 && m.role_key && <LotOverrideSelect projectId={projectId} lotId={lotId} role={m} onChanged={onRefresh} />}
                  </li>
                ))}
              </ul>
            ) : <p className="text-[11px]" style={{ color: '#FDE68A' }}>Matériauthèque vide : l'IA utilisera des défauts sobres{e.style ? ` (ambiance « ${e.style} »)` : ''}.</p>}
            {stage === 'furnished' && e.program_rooms?.length > 0 && <p className="mt-2 text-[10.5px]" style={{ color: 'var(--c-text-faint)' }}>Programme : {e.program_rooms.join(' · ')}</p>}
          </Section>
        )}
        {stage === 'final' && (
          <Section icon={Sun} title="Lumière & abords" testid="inputs-lighting" action={<EditLink onClick={goCfg} label="Paramètres du prompt" testid="edit-settings-link" />}>
            <Row k="Moment" v={e.lighting?.moment_journee} />
            <Row k="Lieu / nord" v={`${e.lighting?.localisation || '—'} · ${e.lighting?.orientation_nord || 0}°`} />
            <Row k="Éclairage artificiel" v={e.lighting?.eclairage || '—'} />
            <Row k="Contexte extérieur" v={e.lighting?.exterior_context || 'aucun — socle neutre, fond blanc'} />
            <Row k="Filigrane" v={e.lighting?.branding_texte || '—'} />
          </Section>
        )}
      </div>
    </div>
  );
};

export default ActiveInputsPanel;
